import cx from 'classnames';
import { useCallback, useContext, useState, useEffect, useMemo } from 'react';

import useHandleInternalLinks from '@/hooks/useHandleInternalLinks';
import SectionProvider from '@/contexts/section-context';
import { LocaleContext } from '@/contexts/locale-context';
import Blocks from '@/next-components/blocks';
import Container from '@/next-components/container';
import { Filters, ResultsInfo, ListShowsEvents } from '@/components/renders';
import { SectionFestival } from '@/components/organisms/SectionFestival/render';
import { formatEventCard, formatFilters } from '@/lib/formaters';
import { sortTerms } from '@/lib/sort-terms';

import { getStyles } from './styles.css';
import { formatImage } from '@/lib/formaters/formatImage';

const ARRAY_SIZE = 6;

const EventListBlock = ({ events }) => (
	<div className="supt-seasonPage__list-shows-events">
		<ListShowsEvents events={events} />
	</div>
);

export default function SeasonPage({ node }) {
	const blocks = node?.blocksJSON ? JSON.parse(node.blocksJSON) : [];
	const blocksCta = blocks.filter(({ name }) => name === 'supt/section-ctas');

	const handleInternalLinks = useHandleInternalLinks();

	const rootClass = cx('supt-seasonPage', getStyles());

	const { __t, locale } = useContext(LocaleContext);

	const [selectedFilters, setSelectedFilters] = useState([]); // Selected filters to filter the list with

	/**
	 * Format Cards + filters
	 */
	const { formatedEvents, filters } = useMemo(() => {
		const events = [];
		const genres = {},
			thematics = {};
		// publics = {};

		if (node.archive?.events?.nodes) {
			node.archive.events.nodes.forEach((evt) => {
				events.push(formatEventCard(evt, locale));
				evt.genres.nodes.forEach((t) => {
					if (!genres[t.id]) genres[t.id] = t;
				});
				evt.thematics.nodes.forEach((t) => {
					if (!thematics[t.id]) thematics[t.id] = t;
				});
				// evt.publics.nodes.forEach((t) => {
				// 	if (!publics[t.id]) publics[t.id] = t;
				// });
			});
		}

		return {
			formatedEvents: events,
			filters: [
				{
					filter: 'genres',
					items: formatFilters(
						Object.values(genres).sort(sortTerms) as FILTER_TERM[]
					),
				},
				{
					filter: 'thematics',
					items: formatFilters(
						Object.values(thematics).sort(
							sortTerms
						) as FILTER_TERM[],
						true
					),
					link: node?.siteSettings?.pageForThematics
						? {
								href: node.siteSettings.pageForThematics.uri,
								title: __t(
									'thematics-filters-link',
									'Discover the themes'
								),
						  }
						: null,
				},
				// {
				// 	filter: 'publics',
				// 	items: formatFilters(
				// 		Object.values(publics).sort(sortTerms),
				// 		true
				// 	),
				// },
			],
		};
	}, [node.archive?.events]);

	useEffect(() => {
		if (location.search === '') return;

		const searchParams = new URLSearchParams(location.search);
		const preSelectedFilters = [];

		searchParams.forEach((value, key) => {
			value.split(',').forEach((id) => {
				preSelectedFilters.push({
					filter: key,
					...(filters
						.find(({ filter }) => filter === key)
						// @ts-ignore ⚠️ Do not convert id to number otherwise it doesn't work anymore
						?.items?.find((item) => item.id === id) ?? {}),
				});
			});
		});
		setSelectedFilters(preSelectedFilters);
	}, []);

	/**
	 * Filter events
	 */
	const filteredEvents = useMemo(() => {
		if (!formatedEvents.length) return [];
		if (!selectedFilters.length) return formatedEvents;

		const selectedGFilters = selectedFilters.filter(
			({ filter }) => filter === 'genres'
		);
		const selectedTFilters = selectedFilters.filter(
			({ filter }) => filter === 'thematics'
		);
		// const selectedPFilters = selectedFilters.filter(
		// 	({ filter }) => filter === 'publics'
		// );

		return formatedEvents.filter((event) => {
			const hasGenres = !!selectedGFilters.length
				? event.genres.some(({ id }) =>
						selectedGFilters.find((g) => g.id === id)
				  )
				: true;
			const hasThematics = !!selectedTFilters.length
				? event.thematics.some(({ id }) =>
						selectedTFilters.find((t) => t.id === id)
				  )
				: true;
			// const hasPublics = !!selectedPFilters.length
			// 	? event.publics.some(({ id }) =>
			// 			selectedPFilters.find((p) => p.id === id)
			// 	  )
			// 	: true;

			return hasGenres && hasThematics /* && hasPublics */;
		});
	}, [formatedEvents, selectedFilters]);

	/**
	 * Update URL according to selected filters
	 */
	useEffect(() => {
		const queryArgs = selectedFilters.reduce((query, item) => {
			if (!query[item.filter]) query[item.filter] = [];
			query[item.filter].push(item.id);
			return query;
		}, {});

		const searchString = decodeURIComponent(
			new URLSearchParams(queryArgs).toString()
		);
		if (searchString !== location.search) {
			history.pushState(
				queryArgs,
				null,
				location.origin +
					location.pathname +
					(searchString !== '' ? '?' + searchString : '')
			);
		}
	}, [selectedFilters]);

	/**
	 * Render event blocks and CTAs
	 */
	const renderEventBlocks = useCallback(() => {
		if (filteredEvents.length === 0) {
			return (
				<ResultsInfo
					number={0}
					onChange={(value) => setSelectedFilters(value)}
				/>
			);
		}

		const elements = [];
		const festivalMap = (node.archive?.festivals?.nodes || []).reduce(
			(acc, fest) => {
				if (fest.slug) acc[fest.slug] = fest;
				return acc;
			},
			{}
		);

		const renderedFestivals = new Set();
		const renderedIndexes = new Set();
		let nonFestivalEvents = [];

		for (let i = 0; i < filteredEvents.length; i++) {
			if (renderedIndexes.has(i)) continue;
			const event = filteredEvents[i];
			const festivalSlug = event.festival;

			if (!festivalSlug) {
				nonFestivalEvents.push(event);
				renderedIndexes.add(i);
			} else if (!renderedFestivals.has(festivalSlug)) {
				// Output any collected non-festival events before the festival section
				if (nonFestivalEvents.length) {
					elements.push(
						<EventListBlock
							key={`events-non-festival-${i}`}
							events={nonFestivalEvents}
						/>
					);
					nonFestivalEvents = [];
				}
				// Render the festival section
				const festEvents = [];
				filteredEvents.forEach((evt, idx) => {
					if (evt.festival === festivalSlug) {
						festEvents.push(evt);
						renderedIndexes.add(idx);
					}
				});
				const festivalSection =
					festivalMap[festivalSlug]?.acf?.festivalSection;
				elements.push(
					<SectionFestival
						key={`festival-${festivalSlug}`}
						title={
							festivalSection?.title ||
							festivalMap[festivalSlug]?.name ||
							festivalSlug
						}
						titleColor={festivalSection?.color}
						titleFont={
							festivalSection?.font
								? {
										desktopSize:
											festivalSection?.font?.desktopSize,
										mobileSize:
											festivalSection?.font?.mobileSize,
										name: festivalSection?.font?.name,
										file: festivalSection?.font?.file?.node,
								  }
								: null
						}
						titleLogo={formatImage(festivalSection?.logo)}
						shows={festEvents}
						link={festivalSection?.link}
					/>
				);
				renderedFestivals.add(festivalSlug);
			}
		}

		// Output any remaining non-festival events at the end
		if (nonFestivalEvents.length) {
			elements.push(
				<EventListBlock
					key="events-non-festival-final"
					events={nonFestivalEvents}
				/>
			);
		}

		// Render all CTAs at the end
		blocksCta.forEach((cta, ctaIndex) => {
			elements.push(
				<Blocks
					key={`cta-extra-${ctaIndex}`}
					blocks={[cta]}
					includes={/^supt\/section-ctas/g}
				/>
			);
		});

		return elements;
	}, [filteredEvents, blocksCta, node.archive?.festivals?.nodes]);

	return (
		<>
			<Container className={rootClass} data-block="seasonPage">
				<div
					className="supt-seasonPage__inner"
					onClick={handleInternalLinks}
				>
					<SectionProvider variant="default">
						<Blocks
							blocks={blocks}
							includes={/^supt\/section-rich-content/g}
						/>
						<Filters
							title={node.title}
							buttons={node.seasonPageCTAs}
							filters={filters}
							selectedFilters={selectedFilters}
							onChange={(value) => setSelectedFilters(value)}
						/>

						{renderEventBlocks()}
					</SectionProvider>
				</div>
			</Container>
		</>
	);
}
